import React, { useState, useEffect } from 'react';
import { Search, Package, CheckCircle, Droplet, HelpCircle, Wrench, RefreshCw, Filter, X, Upload, Image as ImageIcon, Plus, Trash2 } from 'lucide-react';

const ServproEquipment = ({ user }) => {
  const [equipment, setEquipment] = useState([]);
  const [filteredEquipment, setFilteredEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [sortField, setSortField] = useState('asset_tag');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showFilters, setShowFilters] = useState(false);
  const [uploadingImages, setUploadingImages] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [addingEquipment, setAddingEquipment] = useState(false);

  // New equipment form state
  const [newEquipment, setNewEquipment] = useState({
    asset_tag: '',
    inventory_date: '',
    asset_type: '',
    asset_manufacturer: '',
    asset_model_name: '',
    serial_number: '',
    owner_dept: '',
    last_known_dept: '',
    hospital_tag: '',
    vicinity: '',
    item_value: ''
  });

  // Filter states
  const [filters, setFilters] = useState({
    status: '',
    cleaned: '',
    department: '',
    manufacturer: ''
  });

  // Summary stats
  const [stats, setStats] = useState({
    total: 0,
    complete: 0,
    needsCleaning: 0,
    unknown: 0,
    internalCleaning: 0,
    pending: 0,
    cleaned: 0,
    notCleaned: 0
  });

  // Fetch equipment data
  const fetchEquipment = async () => {
    setLoading(true);
    try {
      console.log('=== FETCHING SERVPRO EQUIPMENT ===');
      const response = await fetch('/api/servpro-equipment');
      const data = await response.json();
      
      console.log('Equipment data received:', data);
      setEquipment(data.data || []);
      calculateStats(data.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching servpro equipment:', error);
      setLoading(false);
      setEquipment([]);
    }
  };

  useEffect(() => {
    fetchEquipment();
  }, []);

  // Calculate summary statistics
  const calculateStats = (data) => {
    const total = data.length;
    const complete = data.filter(item => item.status === 'complete').length;
    const needsCleaning = data.filter(item => item.status === 'needs_cleaning').length;
    const unknown = data.filter(item => item.status === 'unknown').length;
    const internalCleaning = data.filter(item => item.status === 'internal_cleaning').length;
    const pending = data.filter(item => item.status === 'pending').length;
    const cleaned = data.filter(item => item.cleaned === 'yes').length;
    const notCleaned = data.filter(item => item.cleaned === 'no').length;

    setStats({
      total,
      complete,
      needsCleaning,
      unknown,
      internalCleaning,
      pending,
      cleaned,
      notCleaned
    });
  };

  // Filter and sort equipment
  const filterAndSortEquipment = () => {
    let filtered = [...equipment];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.asset_tag?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.asset_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.asset_manufacturer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.asset_model_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.serial_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.owner_dept?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.last_known_dept?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.vicinity?.toLowerCase().includes(searchTerm.toLowerCase())  
      );
    }

    // Apply advanced filters
    if (filters.status) {
      filtered = filtered.filter(item => item.status === filters.status);
    }

    if (filters.cleaned) {
      filtered = filtered.filter(item => item.cleaned === filters.cleaned);
    }

    if (filters.department) {
      filtered = filtered.filter(item => 
        item.owner_dept?.toLowerCase().includes(filters.department.toLowerCase()) ||
        item.last_known_dept?.toLowerCase().includes(filters.department.toLowerCase())
      );
    }

    if (filters.manufacturer) {
      filtered = filtered.filter(item => 
        item.asset_manufacturer?.toLowerCase().includes(filters.manufacturer.toLowerCase())
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal = a[sortField] || '';
      let bVal = b[sortField] || '';
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setFilteredEquipment(filtered);
    setCurrentPage(1);
  };

  useEffect(() => {
    filterAndSortEquipment();
  }, [equipment, searchTerm, filters, sortField, sortDirection]);

  // Handle add new equipment
  const handleAddEquipment = async (e) => {
    e.preventDefault();
    
    if (!newEquipment.asset_tag) {
      alert('Asset Tag is required');
      return;
    }

    setAddingEquipment(true);

    try {
      const response = await fetch('/api/servpro-equipment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...newEquipment,
          userName: user?.fullName || user?.username || 'System'
        }),
      });

      const result = await response.json();
      
      if (result.success) {
        alert('Equipment added successfully!');
        setShowAddModal(false);
        // Reset form
        setNewEquipment({
          asset_tag: '',
          inventory_date: '',
          asset_type: '',
          asset_manufacturer: '',
          asset_model_name: '',
          serial_number: '',
          owner_dept: '',
          last_known_dept: '',
          hospital_tag: '',
          vicinity: '', 
          item_value: ''
        });
        // Refresh the list
        fetchEquipment();
      } else {
        alert('Failed to add equipment: ' + result.error);
      }
    } catch (error) {
      console.error('Error adding equipment:', error);
      alert('Failed to add equipment');
    } finally {
      setAddingEquipment(false);
    }
  };

  // Update equipment status
  const updateStatus = async (id, newStatus) => {
    try {
      const response = await fetch(`/api/servpro-equipment/${id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: newStatus,
          userName: user?.fullName || user?.username || 'System'
        }),
      });

      const result = await response.json();
      
      if (result.success) {
        // Update local state
        setEquipment(prevEquipment =>
          prevEquipment.map(item =>
            item.id === id ? { ...item, status: newStatus, status_updated_by: user?.fullName, status_updated_date: new Date() } : item
          )
        );
      } else {
        alert('Failed to update status: ' + result.error);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    }
  };

  // Toggle cleaned status
  const toggleCleaned = async (id, currentCleaned) => {
    const newCleaned = currentCleaned === 'yes' ? 'no' : 'yes';
    
    try {
      const response = await fetch(`/api/servpro-equipment/${id}/cleaned`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          cleaned: newCleaned,
          userName: user?.fullName || user?.username || 'System'
        }),
      });

      const result = await response.json();
      
      if (result.success) {
        // Update local state
        setEquipment(prevEquipment =>
          prevEquipment.map(item =>
            item.id === id ? { ...item, cleaned: newCleaned, status_updated_by: user?.fullName, status_updated_date: new Date() } : item
          )
        );
      } else {
        alert('Failed to update cleaned status: ' + result.error);
      }
    } catch (error) {
      console.error('Error updating cleaned status:', error);
      alert('Failed to update cleaned status');
    }
  };

  // Handle image upload
  const handleImageUpload = async (id, file, imageType) => {
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('File size must be less than 5MB');
      return;
    }

    const formData = new FormData();
    formData.append('image', file);
    formData.append('imageType', imageType);
    formData.append('assetTag', equipment.find(item => item.id === id)?.asset_tag || id);
    formData.append('userName', user?.fullName || user?.username || 'System');

    // Set uploading state
    setUploadingImages(prev => ({ ...prev, [`${id}-${imageType}`]: true }));

    try {
      const response = await fetch(`/api/servpro-equipment/${id}/upload-image`, {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();
      
      if (result.success) {
        // Update local state with the image path
        setEquipment(prevEquipment =>
          prevEquipment.map(item =>
            item.id === id 
              ? { 
                  ...item, 
                  [imageType === 'before' ? 'before_picture' : 'after_picture']: result.imagePath 
                } 
              : item
          )
        );
        alert(`${imageType === 'before' ? 'Before' : 'After'} picture uploaded successfully!`);
      } else {
        alert('Failed to upload image: ' + result.error);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image');
    } finally {
      setUploadingImages(prev => {
        const newState = { ...prev };
        delete newState[`${id}-${imageType}`];
        return newState;
      });
    }
  };

  // Handle file input change
  const handleFileChange = (id, event, imageType) => {
    const file = event.target.files[0];
    if (file) {
      handleImageUpload(id, file, imageType);
    }
  };

  // View image in new window
  const viewImage = (imagePath) => {
    if (imagePath) {
      window.open(imagePath, '_blank');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      complete: '#10b981',
      needs_cleaning: '#3b82f6',
      unknown: '#ef4444',
      internal_cleaning: '#f59e0b',
      pending: '#6b7280'
    };
    return colors[status] || '#6b7280';
  };

  const getRowColor = (item) => {
    if (item.status === 'complete') return '#f0fdf4';
    if (item.status === 'needs_cleaning') return '#eff6ff';
    if (item.status === 'unknown') return '#fef2f2';
    if (item.status === 'internal_cleaning') return '#fffbeb';
    return 'white';
  };

  const formatCurrency = (value) => {
    if (!value) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      cleaned: '',
      department: '',
      manufacturer: ''
    });
    setSearchTerm('');
  };

  // Update vicinity
    const handleUpdateVicinity = async (id, vicinity) => {
      try {
        const response = await fetch(`/api/servpro-equipment/${id}/vicinity`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            vicinity: vicinity,
            userName: user?.fullName || user?.username || 'System'
          }),
        });

        const result = await response.json();
        
        if (result.success) {
          // Update local state
          setEquipment(prevEquipment =>
            prevEquipment.map(item =>
              item.id === id ? { ...item, vicinity: vicinity } : item
            )
          );
        } else {
          alert('Failed to update vicinity: ' + result.error);
        }
      } catch (error) {
        console.error('Error updating vicinity:', error);
        alert('Failed to update vicinity');
      }
    };

    // Delete equipment
    const handleDeleteEquipment = async (id, assetTag) => {
      if (!window.confirm(`Are you sure you want to delete equipment ${assetTag}? This will also delete any associated images and cannot be undone.`)) {
        return;
      }

      try {
        const response = await fetch(`/api/servpro-equipment/${id}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        const result = await response.json();
        
        if (result.success) {
          alert('Equipment deleted successfully!');
          // Remove from local state
          setEquipment(prevEquipment =>
            prevEquipment.filter(item => item.id !== id)
          );
        } else {
          alert('Failed to delete equipment: ' + result.error);
        }
      } catch (error) {
        console.error('Error deleting equipment:', error);
        alert('Failed to delete equipment');
      }
    };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredEquipment.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredEquipment.length / itemsPerPage);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px',
        fontSize: '1.125rem',
        color: '#6b7280'
      }}>
        <RefreshCw className="animate-spin" size={24} style={{ marginRight: '0.5rem' }} />
        Loading equipment...
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem', maxWidth: '1600px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ 
            fontSize: '2rem', 
            fontWeight: '700', 
            color: '#1f2937',
            marginBottom: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <Package size={32} />
            SERVPRO Equipment Tracking
          </h1>
          <p style={{ color: '#6b7280', fontSize: '1rem' }}>
            Track and manage equipment status during restoration process
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          style={{
            padding: '0.75rem 1.5rem',
            background: '#10b981',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontWeight: '600',
            fontSize: '1rem',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          <Plus size={20} />
          Add Equipment
        </button>
      </div>

      {/* Add Equipment Modal */}
      {showAddModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '0.5rem',
            padding: '2rem',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1f2937' }}>Add New Equipment</h2>
              <button
                onClick={() => setShowAddModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.5rem'
                }}
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleAddEquipment}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>
                    Asset Tag <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={newEquipment.asset_tag}
                    onChange={(e) => setNewEquipment({ ...newEquipment, asset_tag: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>
                    Asset Type
                  </label>
                  <input
                    type="text"
                    value={newEquipment.asset_type}
                    onChange={(e) => setNewEquipment({ ...newEquipment, asset_type: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>
                    Manufacturer
                  </label>
                  <input
                    type="text"
                    value={newEquipment.asset_manufacturer}
                    onChange={(e) => setNewEquipment({ ...newEquipment, asset_manufacturer: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>
                    Model Name
                  </label>
                  <input
                    type="text"
                    value={newEquipment.asset_model_name}
                    onChange={(e) => setNewEquipment({ ...newEquipment, asset_model_name: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>
                    Serial Number
                  </label>
                  <input
                    type="text"
                    value={newEquipment.serial_number}
                    onChange={(e) => setNewEquipment({ ...newEquipment, serial_number: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>
                    Owner Department
                  </label>
                  <input
                    type="text"
                    value={newEquipment.owner_dept}
                    onChange={(e) => setNewEquipment({ ...newEquipment, owner_dept: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>
                    Last Known Department
                  </label>
                  <input
                    type="text"
                    value={newEquipment.last_known_dept}
                    onChange={(e) => setNewEquipment({ ...newEquipment, last_known_dept: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>
                    Hospital Tag
                  </label>
                  <input
                    type="text"
                    value={newEquipment.hospital_tag}
                    onChange={(e) => setNewEquipment({ ...newEquipment, hospital_tag: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>
                    Vicinity
                  </label>
                  <input
                    type="text"
                    value={newEquipment.vicinity}
                    onChange={(e) => setNewEquipment({ ...newEquipment, vicinity: e.target.value })}
                    placeholder="e.g., Room 305, West Wing"
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>
                    Item Value ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={newEquipment.item_value}
                    onChange={(e) => setNewEquipment({ ...newEquipment, item_value: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>
                    Inventory Date
                  </label>
                  <input
                    type="date"
                    value={newEquipment.inventory_date}
                    onChange={(e) => setNewEquipment({ ...newEquipment, inventory_date: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#e5e7eb',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '0.375rem',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addingEquipment}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: addingEquipment ? '#9ca3af' : '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.375rem',
                    cursor: addingEquipment ? 'not-allowed' : 'pointer',
                    fontWeight: '500'
                  }}
                >
                  {addingEquipment ? 'Adding...' : 'Add Equipment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', 
        gap: '1rem', 
        marginBottom: '2rem' 
      }}>
        <StatCard icon={Package} label="Total Items" value={stats.total} color="#6b7280" />
        <StatCard icon={CheckCircle} label="Complete" value={stats.complete} color="#10b981" />
        <StatCard icon={Droplet} label="Needs Cleaning" value={stats.needsCleaning} color="#3b82f6" />
        <StatCard icon={HelpCircle} label="Unable to Find" value={stats.unknown} color="#ef4444" />
        <StatCard icon={Wrench} label="Internal Cleaning" value={stats.internalCleaning} color="#f59e0b" />
      </div>

      {/* Search and Filters */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <div style={{ flex: '1', minWidth: '300px', position: 'relative' }}>
            <Search 
              size={20} 
              style={{ 
                position: 'absolute', 
                left: '0.75rem', 
                top: '50%', 
                transform: 'translateY(-50%)',
                color: '#9ca3af'
              }} 
            />
            <input
              type="text"
              placeholder="Search by asset tag, type, manufacturer, model, serial number, or department..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem 0.75rem 0.75rem 2.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '0.5rem',
                fontSize: '0.875rem'
              }}
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            style={{
              padding: '0.75rem 1.5rem',
              background: showFilters ? '#3b82f6' : 'white',
              color: showFilters ? 'white' : '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontWeight: '500'
            }}
          >
            <Filter size={18} />
            Filters
          </button>
          {(searchTerm || Object.values(filters).some(f => f)) && (
            <button
              onClick={clearFilters}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontWeight: '500'
              }}
            >
              <X size={18} />
              Clear
            </button>
          )}
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div style={{
            background: '#f9fafb',
            border: '1px solid #e5e7eb',
            borderRadius: '0.5rem',
            padding: '1.5rem',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem'
          }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem'
                }}
              >
                <option value="">All Statuses</option>
                <option value="complete">Complete</option>
                <option value="needs_cleaning">Needs Cleaning</option>
                <option value="unknown">Unable to Find</option>
                <option value="internal_cleaning">Internal Cleaning</option>
                <option value="pending">Pending</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>
                Cleaned Status
              </label>
              <select
                value={filters.cleaned}
                onChange={(e) => setFilters({ ...filters, cleaned: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem'
                }}
              >
                <option value="">All</option>
                <option value="yes">Cleaned</option>
                <option value="no">Not Cleaned</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>
                Department
              </label>
              <input
                type="text"
                placeholder="Filter by department..."
                value={filters.department}
                onChange={(e) => setFilters({ ...filters, department: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: '#374151' }}>
                Manufacturer
              </label>
              <input
                type="text"
                placeholder="Filter by manufacturer..."
                value={filters.manufacturer}
                onChange={(e) => setFilters({ ...filters, manufacturer: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem'
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Equipment Table */}
      <div style={{ 
        background: 'white', 
        borderRadius: '0.5rem', 
        border: '1px solid #e5e7eb',
        overflow: 'hidden'
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
              <tr>
                <th 
                  onClick={() => handleSort('asset_tag')}
                  style={{ 
                    padding: '1rem', 
                    textAlign: 'left', 
                    fontWeight: '600', 
                    color: '#374151',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  Asset Tag {sortField === 'asset_tag' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  onClick={() => handleSort('asset_type')}
                  style={{ 
                    padding: '1rem', 
                    textAlign: 'left', 
                    fontWeight: '600', 
                    color: '#374151',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  Type {sortField === 'asset_type' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>
                  Model & Serial
                </th>
                <th 
                  onClick={() => handleSort('owner_dept')}
                  style={{ 
                    padding: '1rem', 
                    textAlign: 'left', 
                    fontWeight: '600', 
                    color: '#374151',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  Department {sortField === 'owner_dept' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>
                  Vicinity
                </th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>
                  Value
                </th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>
                  Before Picture
                </th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>
                  After Picture
                </th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {currentItems.length === 0 ? (
                <tr>
                  <td colSpan="9" style={{ 
                    padding: '3rem', 
                    textAlign: 'center', 
                    color: '#6b7280',
                    fontSize: '1rem'
                  }}>
                    No equipment found matching your filters
                  </td>
                </tr>
              ) : (
                currentItems.map((item) => (
                  <tr 
                    key={item.id}
                    style={{ 
                      borderTop: '1px solid #f3f4f6',
                      backgroundColor: getRowColor(item)
                    }}
                  >
                    <td style={{ padding: '0.75rem' }}>
                      <div style={{ fontWeight: '600', color: '#2563eb' }}>{item.asset_tag}</div>
                      {item.hospital_tag && (
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Hospital: {item.hospital_tag}</div>
                      )}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <div style={{ fontWeight: '500' }}>{item.asset_type}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{item.asset_manufacturer}</div>
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <div>{item.asset_model_name}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>SN: {item.serial_number}</div>
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <div style={{ fontWeight: '500' }}>{item.owner_dept}</div>
                      {item.last_known_dept && item.last_known_dept !== item.owner_dept && (
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Last: {item.last_known_dept}</div>
                      )}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <input
                        type="text"
                        value={item.vicinity || ''}
                        onChange={(e) => {
                          // Update local state immediately for responsive UI
                          setEquipment(prevEquipment =>
                            prevEquipment.map(eq =>
                              eq.id === item.id ? { ...eq, vicinity: e.target.value } : eq
                            )
                          );
                        }}
                        onBlur={(e) => handleUpdateVicinity(item.id, e.target.value)}
                        placeholder="Enter vicinity..."
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '0.25rem',
                          fontSize: '0.75rem'
                        }}
                      />
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <div style={{ fontWeight: '500' }}>{formatCurrency(item.item_value)}</div>
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'flex-start' }}>
                        {item.before_picture ? (
                          <>
                            <button
                              onClick={() => viewImage(item.before_picture)}
                              style={{
                                padding: '0.25rem 0.5rem',
                                background: '#10b981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '0.25rem',
                                fontSize: '0.75rem',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem'
                              }}
                            >
                              <ImageIcon size={14} />
                              View
                            </button>
                            <label
                              style={{
                                padding: '0.25rem 0.5rem',
                                background: '#3b82f6',
                                color: 'white',
                                border: 'none',
                                borderRadius: '0.25rem',
                                fontSize: '0.75rem',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem'
                              }}
                            >
                              <Upload size={14} />
                              Replace
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleFileChange(item.id, e, 'before')}
                                style={{ display: 'none' }}
                              />
                            </label>
                          </>
                        ) : (
                          <label
                            style={{
                              padding: '0.25rem 0.5rem',
                              background: uploadingImages[`${item.id}-before`] ? '#9ca3af' : '#3b82f6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '0.25rem',
                              fontSize: '0.75rem',
                              cursor: uploadingImages[`${item.id}-before`] ? 'not-allowed' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem'
                            }}
                          >
                            <Upload size={14} />
                            {uploadingImages[`${item.id}-before`] ? 'Uploading...' : 'Upload'}
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleFileChange(item.id, e, 'before')}
                              style={{ display: 'none' }}
                              disabled={uploadingImages[`${item.id}-before`]}
                            />
                          </label>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'flex-start' }}>
                        {item.after_picture ? (
                          <>
                            <button
                              onClick={() => viewImage(item.after_picture)}
                              style={{
                                padding: '0.25rem 0.5rem',
                                background: '#10b981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '0.25rem',
                                fontSize: '0.75rem',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem'
                              }}
                            >
                              <ImageIcon size={14} />
                              View
                            </button>
                            <label
                              style={{
                                padding: '0.25rem 0.5rem',
                                background: '#3b82f6',
                                color: 'white',
                                border: 'none',
                                borderRadius: '0.25rem',
                                fontSize: '0.75rem',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem'
                              }}
                            >
                              <Upload size={14} />
                              Replace
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleFileChange(item.id, e, 'after')}
                                style={{ display: 'none' }}
                              />
                            </label>
                          </>
                        ) : (
                          <label
                            style={{
                              padding: '0.25rem 0.5rem',
                              background: uploadingImages[`${item.id}-after`] ? '#9ca3af' : '#3b82f6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '0.25rem',
                              fontSize: '0.75rem',
                              cursor: uploadingImages[`${item.id}-after`] ? 'not-allowed' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem'
                            }}
                          >
                            <Upload size={14} />
                            {uploadingImages[`${item.id}-after`] ? 'Uploading...' : 'Upload'}
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleFileChange(item.id, e, 'after')}
                              style={{ display: 'none' }}
                              disabled={uploadingImages[`${item.id}-after`]}
                            />
                          </label>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {/* Status Buttons Row */}
                        <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                          <button
                            onClick={() => updateStatus(item.id, 'complete')}
                            disabled={item.status === 'complete'}
                            style={{
                              padding: '0.25rem 0.5rem',
                              background: item.status === 'complete' ? '#10b981' : '#e5e7eb',
                              color: item.status === 'complete' ? 'white' : '#374151',
                              border: 'none',
                              borderRadius: '0.25rem',
                              fontSize: '0.75rem',
                              cursor: item.status === 'complete' ? 'default' : 'pointer',
                              fontWeight: item.status === 'complete' ? '600' : '400'
                            }}
                            title="Mark as Complete"
                          >
                            ✓ Complete
                          </button>
                          <button
                            onClick={() => updateStatus(item.id, 'needs_cleaning')}
                            disabled={item.status === 'needs_cleaning'}
                            style={{
                              padding: '0.25rem 0.5rem',
                              background: item.status === 'needs_cleaning' ? '#3b82f6' : '#e5e7eb',
                              color: item.status === 'needs_cleaning' ? 'white' : '#374151',
                              border: 'none',
                              borderRadius: '0.25rem',
                              fontSize: '0.75rem',
                              cursor: item.status === 'needs_cleaning' ? 'default' : 'pointer',
                              fontWeight: item.status === 'needs_cleaning' ? '600' : '400'
                            }}
                            title="Needs Cleaning"
                          >
                            🧹 Clean
                          </button>
                          <button
                            onClick={() => updateStatus(item.id, 'unknown')}
                            disabled={item.status === 'unknown'}
                            style={{
                              padding: '0.25rem 0.5rem',
                              background: item.status === 'unknown' ? '#ef4444' : '#e5e7eb',
                              color: item.status === 'unknown' ? 'white' : '#374151',
                              border: 'none',
                              borderRadius: '0.25rem',
                              fontSize: '0.75rem',
                              cursor: item.status === 'unknown' ? 'default' : 'pointer',
                              fontWeight: item.status === 'unknown' ? '600' : '400'
                            }}
                            title="Unable to Find/Locate Equipment"
                          >
                            ⚠ Unable to Find
                          </button>
                          <button
                            onClick={() => updateStatus(item.id, 'internal_cleaning')}
                            disabled={item.status === 'internal_cleaning'}
                            style={{
                              padding: '0.25rem 0.5rem',
                              background: item.status === 'internal_cleaning' ? '#f59e0b' : '#e5e7eb',
                              color: item.status === 'internal_cleaning' ? 'white' : '#374151',
                              border: 'none',
                              borderRadius: '0.25rem',
                              fontSize: '0.75rem',
                              cursor: item.status === 'internal_cleaning' ? 'default' : 'pointer',
                              fontWeight: item.status === 'internal_cleaning' ? '600' : '400'
                            }}
                            title="Internal Cleaning Needed"
                          >
                            🔧 Internal
                          </button>
                        </div>
                        {/* Cleaned Toggle Button */}
                        <button
                          onClick={() => toggleCleaned(item.id, item.cleaned)}
                          style={{
                            padding: '0.25rem 0.5rem',
                            background: item.cleaned === 'yes' ? '#10b981' : item.cleaned === 'no' ? '#ef4444' : '#9ca3af',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.25rem',
                            fontSize: '0.75rem',
                            cursor: 'pointer',
                            fontWeight: '500'
                          }}
                          title="Toggle Cleaned Status"
                        >
                          {item.cleaned === 'yes' ? '✓ Cleaned' : item.cleaned === 'no' ? '✗ Not Cleaned' : 'Set Cleaned Status'}
                        </button>

                        <button
                          onClick={() => handleDeleteEquipment(item.id, item.asset_tag)}
                          style={{
                            padding: '0.25rem 0.5rem',
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.25rem',
                            fontSize: '0.75rem',
                            cursor: 'pointer',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem'
                          }}
                          title="Delete Equipment"
                        >
                          <Trash2 size={12} />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ padding: '1rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              Page {currentPage} of {totalPages}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                style={{
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  background: 'white',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  opacity: currentPage === 1 ? 0.5 : 1
                }}
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                style={{
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  background: 'white',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  opacity: currentPage === totalPages ? 0.5 : 1
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, color }) => (
  <div style={{
    background: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '0.5rem',
    padding: '1.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '1rem'
  }}>
    <div style={{
      width: '48px',
      height: '48px',
      borderRadius: '0.5rem',
      background: `${color}15`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0
    }}>
      <Icon size={24} color={color} />
    </div>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: '1.875rem', fontWeight: '700', color: '#1f2937', lineHeight: 1 }}>
        {value}
      </div>
      <div style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: '500', marginTop: '0.25rem' }}>
        {label}
      </div>
    </div>
  </div>
);

export default ServproEquipment;